import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PaginationOptions } from '@shared/models/pagination-options.class';
import { Packages } from '@shared/enums/packages.enum';
import { Helpers } from '@shared/helpers/helpers';

@Injectable({
	providedIn: 'root',
})
export class CacheService {
	private DEFAULT_PAGE_INDEX = Helpers.DEFAULT_PAGE_INDEX;
	private DEFAULT_PAGE_SIZE = Helpers.DEFAULT_PAGE_SIZE;

	private paginationSettings = new BehaviorSubject<any>(null);
	paginationSettingsChanges$ = this.paginationSettings.asObservable();

	private previousTableScope: string;
	private currentTableScope: string;
	private currentTableName: string;
	private previousPackageName: Packages;
	private currentPackageName: Packages;

	private cacheItems: Map<string, any> = new Map<string, any>();

	changePaginationSettings(paginationOptions: PaginationOptions) {
		let paginationSettings = this.getPaginationSettings();

		if (!paginationSettings) {
			paginationSettings = {};
		} else {
			paginationSettings[this.currentTableName] = paginationOptions;
		}

		this.paginationSettings.next(paginationSettings);
	}

	getPaginationSettings() {
		return this.paginationSettings.value;
	}

	getTablePaginationSettings() {
		const paginationSettings = this.paginationSettings.value;
		if (paginationSettings && paginationSettings[this.currentTableName]) {
			return paginationSettings[this.currentTableName];
		}
		return null;
	}

	loadPaginationOptions(pageIndex?: number, pageSize?: number) {
		// If the user is in the same table scope and package, try to get stored pagination options
		if (
			this.previousTableScope &&
			this.previousTableScope === this.currentTableScope &&
			this.previousPackageName &&
			this.previousPackageName === this.currentPackageName
		) {
			const paginationSettings = this.getTablePaginationSettings();
			if (paginationSettings && Object.keys(paginationSettings).length > 0) {
				return paginationSettings;
			}
		}

		// If no previous settings were found, return new ones
		const newPaginationOptions = new PaginationOptions(pageIndex || this.DEFAULT_PAGE_INDEX, pageSize || this.DEFAULT_PAGE_SIZE);
		this.changePaginationSettings(newPaginationOptions);
		return newPaginationOptions;
	}

	resetPaginationSettings() {
		this.paginationSettings.next(null);
	}

	/* TODO: This routerEvent has the URL of the navigation. It could be used to know when a user goes up or down. The idea was to allow infinite
	 * number of nested tables, and store all of them unless the user "goes up", since that should reset the table they are leaving.
	 * It does have some problems, like: what happens when a user leaves a tableName scope to go into another from a different "branch" without going
	 * through the main table? (since that would remove all nested tables)
	 *
	 * 14/01/2019: A new NavigationService has been made and you can get the active route from there, it could be helpful for this thing as well
	 */
	registerContext(event, routerEvent) {
		this.registerTableScope(event.tableScope);
		this.registerTableName(event.tableName, event.tableScope);
		this.registerPackageName(event.packageName);
	}

	registerTableScope(tableScope: string) {
		this.previousTableScope = this.currentTableScope;
		this.currentTableScope = tableScope;
	}

	registerTableName(tableName: string, tableScope: string) {
		if (tableName) {
			this.currentTableName = tableName;
		} else {
			this.currentTableName = tableScope;
			// If there's no tableName, the user has navigated "up", so reset nested table
			this.resetTables(tableScope);
		}
	}

	registerPackageName(packageName: Packages) {
		this.previousPackageName = this.currentPackageName;
		this.currentPackageName = packageName;
	}

	// This deletes from the cache every table that's not the tableScope one, it's used both when going "up" from a nested table and
	// when changing to a different tableScope
	resetTables(tableScope) {
		const paginationSettings = this.getPaginationSettings();

		if (paginationSettings) {
			for (const key of Object.keys(paginationSettings)) {
				if (key !== tableScope) {
					delete paginationSettings[key];
				}
			}
		}
	}

	load(key: string): any {
		return this.cacheItems.has(key) ? this.cacheItems.get(key) : null;
	}

	save(key: string, value: any) {
		this.cacheItems.set(key, value);
	}
}
